import React, { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { api } from './lib/api';
import { useCompany } from './contexts/CompanyContext';
import { Link } from 'react-router-dom';
import type { Transaction } from './lib/api-types';
import OperationViewModal from './OperationViewModal';
import OperationEditModal from './OperationEditModal';

interface OperationsListResponse {
  total: number;
  items: Transaction[];
  skip: number;
  take: number;
}

const OperationsList: React.FC = () => {
  const { entreprises } = useCompany();
  const queryClient = useQueryClient();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [viewOperation, setViewOperation] = useState<Transaction | null>(null);
  const [editOperation, setEditOperation] = useState<Transaction | null>(null);

  // For now, assume first entreprise and its first exercice
  const entrepriseId = entreprises[0]?.id;
  const exercice = entreprises[0]?.exercices?.find(ex => ex.is_current);
  const exerciceId = exercice?.id;

  const { data, isLoading, error } = useQuery<OperationsListResponse>({
    queryKey: ['operations', entrepriseId],
    queryFn: () => api.transactions.list(entrepriseId!, { type: 'operation' }),
    enabled: !!entrepriseId,
  });

  const operations = data?.items || [];

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.transactions.delete(id, entrepriseId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['operations', entrepriseId] });
    },
  });

  const batchValidateMutation = useMutation({
    mutationFn: (ids: string[]) => api.transactions.batchValidate(ids, entrepriseId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['operations', entrepriseId] });
    },
  });

  const handleDelete = async (id: string) => {
    if (confirm('Êtes-vous sûr de vouloir supprimer cette opération ?')) {
      await deleteMutation.mutateAsync(id);
    }
  };

  const handleBatchValidate = async () => {
    if (selectedIds.size > 0) {
      await batchValidateMutation.mutateAsync(Array.from(selectedIds));
      setSelectedIds(new Set());
    }
  };

  if (!entrepriseId || !exerciceId) {
    return <div className="text-center py-12 text-gray-500">Configuration incomplète</div>;
  }

  if (isLoading) {
    return <div className="text-center py-12">Chargement...</div>;
  }

  if (error) {
    return <div className="text-center py-12 text-red-500">Erreur de chargement</div>;
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Page header */}
      <p className="text-[10px] uppercase text-purple-500 tracking-widest font-semibold">
        Comptabilité
      </p>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Opérations complexes</h2>

      {/* Actions */}
      <div className="flex justify-between items-center mb-6">
        <Link
          to="/saisie"
          className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors"
        >
          + Nouvelle opération
        </Link>

        {selectedIds.size > 0 && (
          <button
            onClick={handleBatchValidate}
            className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
          >
            Valider sélection ({selectedIds.size})
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                <input
                  type="checkbox"
                  checked={selectedIds.size === operations.length && operations.length > 0}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedIds(new Set(operations.map(op => op.id)));
                    } else {
                      setSelectedIds(new Set());
                    }
                  }}
                  className="rounded border-gray-300"
                />
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Libellé</th>
              <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Statut</th>
              <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {operations.map((operation) => (
              <tr key={operation.id} className="border-t border-gray-100">
                <td className="px-6 py-4">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(operation.id)}
                    onChange={(e) => {
                      const newSelected = new Set(selectedIds);
                      if (e.target.checked) {
                        newSelected.add(operation.id);
                      } else {
                        newSelected.delete(operation.id);
                      }
                      setSelectedIds(newSelected);
                    }}
                    className="rounded border-gray-300"
                  />
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  {new Date(operation.date).toLocaleDateString('fr-FR')}
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  {operation.libelle}
                </td>
                <td className="px-6 py-4 text-center">
                  {operation.statut_validation === 'validated' ? (
                    <span className="text-green-600 text-lg font-bold">✓ Validée</span>
                  ) : (
                    <button
                      onClick={() => {
                        if (confirm('Valider cette opération ?')) {
                          batchValidateMutation.mutate([operation.id]);
                        }
                      }}
                      className="px-3 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded hover:bg-gray-200 transition-colors"
                    >
                      Valider
                    </button>
                  )}
                </td>
                <td className="px-6 py-4 text-center">
                  <div className="flex justify-center gap-2">
                    <button
                      onClick={() => setViewOperation(operation)}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                      title="Voir"
                    >
                      👁️
                    </button>
                    {operation.statut_validation !== 'validated' && (
                      <button
                        onClick={() => setEditOperation(operation)}
                        className="text-orange-600 hover:text-orange-800 text-sm"
                        title="Modifier"
                      >
                        ✏️
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(operation.id)}
                      className="text-red-600 hover:text-red-800 text-sm"
                      title="Supprimer"
                    >
                      🗑️
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {operations.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          Aucune opération trouvée. <Link to="/saisie" className="text-purple-600 hover:text-purple-800">Créer la première</Link>
        </div>
      )}

      {/* Modals */}
      {viewOperation && (
        <OperationViewModal
          operation={viewOperation}
          onClose={() => setViewOperation(null)}
        />
      )}

      {editOperation && (
        <OperationEditModal
          operation={editOperation}
          entrepriseId={entrepriseId!}
          onClose={() => setEditOperation(null)}
          onSuccess={() => {
            setEditOperation(null);
            queryClient.invalidateQueries({ queryKey: ['operations', entrepriseId] });
          }}
        />
      )}
    </div>
  );
};

export default OperationsList;